import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import { supabase } from '../config/supabase';
import { useAuth } from '../auth/AuthContext';

type AccountStatus = {
  email: string;
  pendingEmail: string | null;
  displayName: string;
  role: string;
  linkedPlayerName: string | null;
  emailCodeAvailable: boolean;
  passwordStatus: 'set' | 'not_set';
  passwordSetAt: string | null;
};

async function authHeaders(): Promise<Record<string, string>> {
  const token = (await supabase.auth.getSession()).data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function AccountManagement() {
  const { completePasswordSetup, signOut } = useAuth();
  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/account/status', { headers: await authHeaders() });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to load account status.');
      const nextStatus = result as AccountStatus;
      setStatus(nextStatus);
      setNewEmail(nextStatus.pendingEmail || nextStatus.email || '');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load account status.');
    } finally {
      setLoading(false);
    }
  };

  const submitEmailChange = async (event: FormEvent) => {
    event.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch('/api/account/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ email }),
      });
      const result = await response.json().catch(() => ({})) as { error?: string; alreadyCurrent?: boolean };
      if (!response.ok) throw new Error(result.error || 'Unable to request an email change.');
      setSuccess(result.alreadyCurrent
        ? 'That is already your sign-in email.'
        : 'Verification email sent. Your sign-in email will change after you confirm the new address.');
      await loadStatus();
    } catch (changeError) {
      setError(changeError instanceof Error ? changeError.message : 'Unable to request an email change.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    void loadStatus();
  }, []);

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < 8) {
      setError('Password must contain at least 8 characters.');
      return;
    }
    if (password !== confirmation) {
      setError('The passwords do not match.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await completePasswordSetup(password);
      setPassword('');
      setConfirmation('');
      setSuccess('Your password has been updated. Email code sign-in remains available.');
      await loadStatus();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to update your password.');
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch('/api/account/delete', {
        method: 'POST',
        headers: await authHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Unable to delete your account.');
      await signOut();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Unable to delete your account.');
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Account Management</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Manage your sign-in options and CourtCaptain account.
      </Typography>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Account</Typography>
            <Stack spacing={0.75}>
              <Typography><strong>Name:</strong> {status?.displayName || '-'}</Typography>
              <Typography><strong>Email:</strong> {status?.email || '-'}</Typography>
              {status?.pendingEmail && <Typography color="warning.main"><strong>Pending email:</strong> {status.pendingEmail}</Typography>}
              <Typography><strong>Role:</strong> {status?.role || '-'}</Typography>
              {status?.linkedPlayerName && <Typography><strong>Linked Player:</strong> {status.linkedPlayerName}</Typography>}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Box component="form" onSubmit={submitEmailChange}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Change Sign-In Email</Typography>
                  <Typography color="text.secondary">
                    {status?.linkedPlayerName
                      ? 'This is also the email on your Player profile. We will keep your current sign-in email active until you verify the new one.'
                      : 'We will keep your current sign-in email active until you verify the new one.'}
                  </Typography>
                </Box>
                <TextField label="New Email" type="email" autoComplete="email" value={newEmail} onChange={(event) => setNewEmail(event.target.value)} required />
                <Button type="submit" variant="contained" disabled={saving} sx={{ alignSelf: 'flex-start' }}>
                  {saving ? <CircularProgress color="inherit" size={20} /> : status?.pendingEmail ? 'Resend Verification Email' : 'Send Verification Email'}
                </Button>
              </Stack>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Sign-In & Security</Typography>
                <Typography color="text.secondary">
                  Email code sign-in is available. Password sign-in is {status?.passwordStatus === 'set' ? 'enabled' : 'not set up'}.
                </Typography>
              </Box>
              <Divider />
              <Box component="form" onSubmit={submitPassword}>
                <Stack spacing={2}>
                  <TextField label="New Password" type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
                  <TextField label="Confirm Password" type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required />
                  <Button type="submit" variant="contained" startIcon={<LockResetIcon />} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
                    {saving ? <CircularProgress color="inherit" size={20} /> : status?.passwordStatus === 'set' ? 'Change Password' : 'Set Password'}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Delete Account</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              This removes your CourtCaptain login account. Player records, rosters, results, payments, and historical team data are preserved.
            </Typography>
            <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}>
              Delete My Account
            </Button>
          </Paper>
        </Stack>
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Delete account?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              Your login will be disabled, but CourtCaptain will keep player records and team history intact.
            </Alert>
            <TextField
              label="Type DELETE to confirm"
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteAccount} disabled={saving || deleteConfirmation !== 'DELETE'}>
            {saving ? <CircularProgress color="inherit" size={20} /> : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
